import { ProjectAnalysisTab, ProjectAnalysisTabs } from "@/types/projectAnalysisTab";
import { FeatureManagementType, ModelScopeSettingsItemRespone } from "@api/apiClient";
import { useAppSettings } from "@api/appSettings";
import { useCadModelCategories } from "@api/cadModelCategories";
import { useComponents, useElements, useModelScopes } from "@api/models";
import { useProject } from "@api/projects";
import { useScenarios } from "@api/scenarios";
import ProjectStepper from "@components/ProjectStepper/ProjectStepper";
import { SplitPane, Splitter } from "@components/Splitter/Splitter";
import useLmViewers from "@features/analysis/hooks/useLmViewers";
import useProjectAnalysisTabs from "@features/analysis/hooks/useProjectAnalysisTabs";
import useAppStore from "@hooks/useAppStore";
import useScopeStore from "@hooks/useScopeStore";
import useSelectionManager from "@hooks/useSelectionManager";
import useUserPermissionStore from "@hooks/useUserPermissionStore";
import i18n from "@i18n/i18n";
import ViewInArIcon from "@mui/icons-material/ViewInAr";
import { ToggleButtonGroup, useTheme } from "@mui/material";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import ToggleButton from "@mui/material/ToggleButton";
import Tooltip from "@mui/material/Tooltip";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import waitForElm from "@utils/waitForElem";
import { observer } from "mobx-react";
import { SyntheticEvent, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AdditionTab from "../AdditionTab/AdditionTab";
import MuiAssignTab from "../MuiAssignTab/MuiAssignTab";
import MuiModelTab from "../MuiModelTab/MuiModelTab";
import ResultTab from "../ResultsTab/ResultsTab";
import ScenariosTab from "../ScenariosTab/ScenariosTab";

function a11yProps(index: any) {
  return {
    id: `tabbar-tab-${index}`,
    "aria-controls": `tabbar-tabpanel-${index}`,
  };
}

const ProjectAnalysisTabBar = observer(() => {
  console.log("ProjectAnalysisTabBar render");

  const theme = useTheme();
  const appStore = useAppStore();
  const selectionManager = useSelectionManager();
  const { id: projectId } = useParams();
  const [activeTab, setActiveTab] = useState(0);
  const [enableViewer1, setEnableViewer1] = useState(true);
  const [enableViewer2, setEnableViewer2] = useState(false);
  const [showViewer2, setShowViewer2] = useState(false);
  const [defaultTab] = useState(+window.location.hash?.replace("#", "") || 0);
  const navigate = useNavigate();
  const [defaultSizes] = useState(appStore.projectTabBarSizes);
  const collapseSizes = [0, 100];
  const upStore = useUserPermissionStore();
  const appSettings = useAppSettings();
  useLmViewers({
    activeTab,
    enableViewer1,
    enableViewer2,
    showViewer2,
  });
  const project = useProject(projectId!);
  useElements(selectionManager.selectedModel?.modelId, selectionManager.selectedScenarioId, { enabled: false });
  useComponents(selectionManager.selectedModel?.modelId, selectionManager.selectedScenarioId, { enabled: false });
  const scopeStore = useScopeStore();
  const scenarios = useScenarios(projectId!);
  useCadModelCategories({ enabled: false });
  const modelScopes = useModelScopes(selectionManager.selectedModel?.modelId, { enabled: false });
  const projectAnalysisTabs = useProjectAnalysisTabs();
  //const co2MaterialTypes = useCo2MaterialTypes(undefined, false, { enabled: false });

  useEffect(() => {
    // TODO: test this with a random guid in the  url
    selectionManager.setProject(project.data?.project);
    upStore.setResourcePermissions(project.data?.permissions);
  }, [project.data]);

  useEffect(() => {
    selectionManager.setScenarios(scenarios.data);
  }, [scenarios.data]);

  useEffect(() => {
    if (modelScopes.data) {
      scopeStore.setModelScope(selectionManager.selectedModel?.modelId!, modelScopes.data);
    }
  }, [modelScopes.data]);

  useEffect(() => {
    scopeStore.setScopes(appSettings.data?.modelScopeSetting?.modelScopeSettingsItems?.filter((item: ModelScopeSettingsItemRespone) => !item.isDeleted) || []);
    // Make all scopes selected by default
    scopeStore.setSelectedScopeIds(scopeStore.scopes?.map((s) => s.id || 0));
  }, [appSettings.data]);

  const handleTabChange = async (_event: SyntheticEvent, newValue: any) => {
    window.location.hash = newValue;
    setActiveTab(newValue);

    // TODO: this should really be done based on selectionManager.selectionMode === 'DualScenarios'
    if (projectAnalysisTabs.tabs.at(newValue) === ProjectAnalysisTabs.Results) {
      setEnableViewer2(true);
    } else {
      setShowViewer2(false);
      setEnableViewer2(false);
    }

    // Reset selections when switching tabs via SelectionManager API
    await selectionManager.clearSelection();
  };

  const handleResultsTabSelectChange = async () => {
    const selectedScenarioIds = selectionManager.scenarioIds;
    console.log("handleResultsTabSelectChange", selectedScenarioIds);

    if (projectAnalysisTabs.tabs.at(activeTab) === ProjectAnalysisTabs.Results && selectedScenarioIds.length > 1) {
      setShowViewer2(true);
    }

    // Handle viewer1 and viewer2 swapping
    if (selectedScenarioIds.length === 1) {
      const scenarioId = (document.querySelector("#lmViewer1")?.firstChild as HTMLElement)?.getAttribute("data-scenario-id");
      if (scenarioId !== null && scenarioId !== selectedScenarioIds[0]) {
        const element1 = document.querySelector("#lmViewer1") as HTMLElement;
        if (element1) {
          const viewer1 = document.querySelector("#lmViewer1")?.firstChild as HTMLElement;
          const viewer2 = document.querySelector("#lmViewer2")?.firstChild as HTMLElement;
          if (viewer2) {
            const container = viewer1?.id === "lmViewer1-viewer" ? (appStore.lmViewer2?.viewer?.container as HTMLElement) : (appStore.lmViewer1?.viewer?.container as HTMLElement);
            if (container) {
              element1?.removeChild(viewer1);
              element1?.appendChild(container);
            }
          } else if (viewer1?.id === "lmViewer2-viewer") {
            const container = appStore.lmViewer1?.viewer?.container as HTMLElement;
            if (container) {
              element1?.removeChild(viewer1);
              element1?.appendChild(container);
              appStore.lmViewer1?.viewer.resize();
            }
          }
        }
      }
    } else if (selectedScenarioIds.length === 2) {
      const viewer1 = document.querySelector("#lmViewer1")?.firstChild as HTMLElement;
      const scenarioId = viewer1?.getAttribute("data-scenario-id");
      if (scenarioId !== selectedScenarioIds[0]) {
        try {
          await waitForElm("#lmViewer1");
          await waitForElm("#lmViewer1-viewer", 2000);
          await waitForElm("#lmViewer2");
          await waitForElm("#lmViewer2-viewer", 2000);
        } catch {}

        const newViewer1 = viewer1.id === "lmViewer1-viewer" ? (appStore.lmViewer2?.viewer?.container as HTMLElement) : (appStore.lmViewer1?.viewer?.container as HTMLElement);
        const newViewer2 = viewer1.id === "lmViewer1-viewer" ? (appStore.lmViewer1?.viewer?.container as HTMLElement) : (appStore.lmViewer2?.viewer?.container as HTMLElement);

        document.querySelector("#lmViewer1")?.appendChild(newViewer1);
        document.querySelector("#lmViewer2")?.appendChild(newViewer2);
      } else {
        try {
          await waitForElm("#lmViewer2");
          await waitForElm("#lmViewer1-viewer", 2000);
          await waitForElm("#lmViewer2-viewer", 2000);
        } catch {}

        const newViewer2 = viewer1.id === "lmViewer1-viewer" ? (appStore.lmViewer2?.viewer?.container as HTMLElement) : (appStore.lmViewer1?.viewer?.container as HTMLElement);
        document.querySelector("#lmViewer2")?.appendChild(newViewer2);
      }
    }

    (document.querySelector("#lmViewer1")?.firstChild as HTMLElement)?.setAttribute("data-scenario-id", selectedScenarioIds[0] ?? "");
    (document.querySelector("#lmViewer2")?.firstChild as HTMLElement)?.setAttribute("data-scenario-id", selectedScenarioIds[1] ?? "");

    if (projectAnalysisTabs.tabs.at(activeTab) === ProjectAnalysisTabs.Results && selectedScenarioIds.length <= 1) {
      setShowViewer2(false); // TODO:
    }
  };

  useEffect(() => {
    navigate(window.location.pathname);
    window.location.hash = defaultTab.toString();
    setActiveTab(defaultTab);
    setEnableViewer2(projectAnalysisTabs.tabs.at(defaultTab) === ProjectAnalysisTabs.Results);
  }, [defaultTab]);

  useEffect(() => {
    if (window.location.hash !== `#${activeTab}`) {
      setActiveTab(+window.location.hash?.replace("#", "") || 0);
    }
  }, [window.location.hash]);

  useEffect(() => {
    return () => {
      console.log("ProjectAnalysisTabBar unmount");
    };
  }, []);

  const onDragEnd = (event: any) => {
    console.log(event);
    appStore.setProjectTabBarSizes(event);
    appStore.lmViewer1?.viewer.resize();
    appStore.lmViewer2?.viewer.resize();
  };

  // Syncronize the views with selectedScenarios
  // Unload any urns that are not in selectedScenarios[0] and appStore.
  return (
    <>
      <ProjectStepper />
      <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
        <Tabs
          value={activeTab}
          onChange={(e, v) => handleTabChange(e, v)}
          sx={{
            flexShrink: 0,
            // @ts-ignore Property 'custom' does not exist on type 'Theme'
            "& .MuiButtonBase-root": { backgroundColor: theme.custom.tabs.background },
            "& .MuiButtonBase-root.Mui-selected": {
              backgroundColor: theme.palette.action.selected,
            },
            "& .MuiTabs-indicator": {
              top: 0,
              bottom: "unset",
              backgroundColor: theme.palette.grey[600],
            },
          }}
        >
          <Tab key={ProjectAnalysisTab.Models} label={i18n.t("project-tab-bar.tabs.models")} {...a11yProps(ProjectAnalysisTabs.Models)} />
          <Tab key={ProjectAnalysisTab.Scenarios} label={i18n.t("project-tab-bar.tabs.scenarios")} {...a11yProps(ProjectAnalysisTabs.Scenarios)} />
          <Tab key={ProjectAnalysisTab.Assign} label={i18n.t("project-tab-bar.tabs.assign")} {...a11yProps(ProjectAnalysisTabs.Assign)} />
          <Tab key={ProjectAnalysisTab.Results} label={i18n.t("project-tab-bar.tabs.results")} {...a11yProps(ProjectAnalysisTabs.Results)} />
          {appStore.hasFeature(FeatureManagementType.AdHocElements) && (
            <Tab key={ProjectAnalysisTab.Additions} label={i18n.t("project-tab-bar.tabs.addition")} {...a11yProps(ProjectAnalysisTabs.Additions)} />
          )}
        </Tabs>

        <Box mr={2} sx={{ flexGrow: 1, display: "flex", justifyContent: "end", alignItems: "center", gap: 1 }}>
          <ToggleButtonGroup>
            <Tooltip title={appStore.isLmViewerEnabled ? i18n.t("project-tab-bar.hide-3d-viewer") : i18n.t("project-tab-bar.show-3d-viewer")}>
              <ToggleButton value="viewer" selected={appStore.isLmViewerEnabled} onChange={() => appStore.setIsLmViewerEnabled(!appStore.isLmViewerEnabled)} size="small">
                <ViewInArIcon fontSize="small" />
              </ToggleButton>
            </Tooltip>
            {/* <Tooltip title={appStore.isInventoryEnabled ? i18n.t("project-tab-bar.hide-table-panel") : i18n.t("project-tab-bar.show-table-panel")}>
              <ToggleButton value="table" selected={appStore.isInventoryEnabled} onChange={() => appStore.setIsInventoryEnabled(!appStore.isInventoryEnabled)} size="small">
                <ViewListOutlinedIcon fontSize="small" />
              </ToggleButton>
            </Tooltip> */}
          </ToggleButtonGroup>
        </Box>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", flexGrow: 1, width: "99.7%" }}>
        {appStore.isLmViewerEnabled && appStore.isInventoryEnabled && (
          <Splitter onDragEnd={(e: any) => onDragEnd(e)} minSize={0} sizes={defaultSizes} style={{ display: "flex", flexDirection: "row", flexGrow: 1 }}>
            <SplitPane style={{ minWidth: 0 }}>
              <ViewerPane showViewer2={showViewer2} />
            </SplitPane>
            <SplitPane style={{ position: "relative", minWidth: 300 }}>
              <InventoryPane activeTab={activeTab} projectAnalysisTabs={projectAnalysisTabs} onResultsSelectChange={handleResultsTabSelectChange} />
            </SplitPane>
          </Splitter>
        )}
        {appStore.isLmViewerEnabled && !appStore.isInventoryEnabled && (
          <Box sx={{ display: "flex", flexDirection: "row", flexGrow: 1, width: "100%" }}>
            <ViewerPane showViewer2={showViewer2} />
          </Box>
        )}
        {!appStore.isLmViewerEnabled && appStore.isInventoryEnabled && (
          <Box sx={{ display: "flex", flexDirection: "row", flexGrow: 1, position: "relative", width: "100%" }}>
            <InventoryPane activeTab={activeTab} projectAnalysisTabs={projectAnalysisTabs} onResultsSelectChange={handleResultsTabSelectChange} />
          </Box>
        )}
      </Box>
    </>
  );
});

const TabPanel = observer((props: any) => {
  const { children, value, index, ...other } = props;
  const isActive = value === index;

  return (
    <Box
      key={`tab-${index}`}
      role="tabpanel"
      hidden={!isActive}
      id={`tabbar-tabpanel-${index}`}
      aria-labelledby={`tabbar-tab-${index}`}
      {...other}
      sx={{
        height: "100%",
        maxWidth: "100vw",
        minWidth: "100%",
        display: isActive ? "block" : "none",
      }}
    >
      {isActive ? children : null}
    </Box>
  );
});

const ViewerPane = ({ showViewer2 }: { showViewer2: boolean }) => (
  <Stack spacing={1} sx={{ width: "100%", height: "100%" }}>
    <Box id="lmViewer1" style={{ position: "relative", width: "100%", height: showViewer2 ? "50%" : "100%" }}></Box>
    {showViewer2 && <Box id="lmViewer2" style={{ position: "relative", width: "100%", height: "50%" }}></Box>}
  </Stack>
);

interface InventoryPaneProps {
  activeTab: number;
  projectAnalysisTabs: ReturnType<typeof useProjectAnalysisTabs>;
  onResultsSelectChange: () => void;
}

const InventoryPane = observer(({ activeTab, projectAnalysisTabs, onResultsSelectChange }: InventoryPaneProps) => {
  const appStore = useAppStore();
  const assignGridApiRef = useGridApiRef();

  return (
    <>
      <TabPanel key={ProjectAnalysisTab.Models} value={activeTab} index={projectAnalysisTabs.getTabIndex(ProjectAnalysisTab.Models)}>
        <MuiModelTab />
      </TabPanel>
      <TabPanel key={ProjectAnalysisTab.Scenarios} value={activeTab} index={projectAnalysisTabs.getTabIndex(ProjectAnalysisTab.Scenarios)}>
        <ScenariosTab />
      </TabPanel>
      <TabPanel key={ProjectAnalysisTab.Assign} value={activeTab} index={projectAnalysisTabs.getTabIndex(ProjectAnalysisTab.Assign)}>
        <MuiAssignTab assignGridApiRef={assignGridApiRef} />
      </TabPanel>
      <TabPanel key={ProjectAnalysisTab.Results} value={activeTab} index={projectAnalysisTabs.getTabIndex(ProjectAnalysisTab.Results)}>
        <ResultTab assignGridApiRef={assignGridApiRef} onSelectChange={onResultsSelectChange} />
      </TabPanel>
      {appStore.hasFeature(FeatureManagementType.AdHocElements) && (
        <TabPanel key={ProjectAnalysisTab.Additions} value={activeTab} index={projectAnalysisTabs.getTabIndex(ProjectAnalysisTab.Additions)}>
          <AdditionTab />
        </TabPanel>
      )}
    </>
  );
});

export default ProjectAnalysisTabBar;
